import { Button, Spinner, mergeClasses } from "@fluentui/react-components";
import {
  ArrowClockwiseRegular,
  ArrowDownloadRegular,
  CheckmarkCircleFilled,
  DismissRegular,
  ErrorCircleRegular,
  SearchRegular,
  SparkleRegular,
  WarningRegular,
} from "@fluentui/react-icons";
import {
  getProjectIqContextFileTags,
  getProjectIqWorkspaceDocuments,
} from "apps/project-iq/api";
import { SourceDocumentPickerDialog } from "apps/project-iq/components/SourceDocumentPickerDialog/SourceDocumentPickerDialog";
import { SourceDocumentTypeBadge } from "apps/project-iq/components/SourceDocumentTypeBadge/SourceDocumentTypeBadge";
import {
  ISowAnalysis,
  SowAnalysisStatus,
} from "apps/project-iq/components/WorkspacePlan/WorkspacePlan.types";
import { IEngagementSourceDocument } from "apps/project-iq/models/IEngagementSourceDocument";
import {
  getDocumentTypeLabel,
  getFullFolderPathLabel,
} from "apps/project-iq/utils/engagementSourceDocumentUtils";
import { IContextFileTag } from "models/IContextFileTag";
import { useEffect, useId, useMemo, useRef, useState } from "react";
import { useQuery } from "react-query";
import { useImportEngagementItemsStyles } from "./ImportEngagementItems.styles";
import { IImportEngagementItemsProps } from "./ImportEngagementItems.types";

const ENGAGEMENT_SOURCE_DOCUMENTS_QUERY_KEY = "engagement-source-documents";
const CONTEXT_FILE_TAGS_QUERY_KEY = "context-file-tags";
const DISMISSED_RESULT_STORAGE_KEY_PREFIX =
  "project-iq:import-result-dismissed";
const PROCESSING_STATUS: SowAnalysisStatus = "processing";
const RESULT_STATUSES: SowAnalysisStatus[] = [
  "completed",
  "failed",
  "skipped_no_content",
];
const DESCRIPTION_TEXT =
  "Draft milestones, deliverables and RAID log entries from the SOW, proposal, change orders, extensions, or purchase orders. Entries your team has added or reviewed are never overwritten.";
const IMPORT_BUTTON_LABEL = "Import";
const BROWSE_BUTTON_LABEL = "Browse Context & Files";
const CHECK_STATUS_LABEL = "Check status";
const CLEAR_SELECTION_LABEL = "Remove selected document";
const DISMISS_RESULT_LABEL = "Dismiss import result";
const IMPORT_FAILED_FALLBACK_MESSAGE = "Unknown error.";
const IMPORT_TIMED_OUT_MESSAGE =
  "The import is taking longer than expected. Check status to see whether it has finished.";
const UNREACHABLE_MESSAGE =
  "Can't reach the server to check the import. Check your connection, then check status again.";
const NO_CONTENT_MESSAGE = "No extractable content was found in this document.";
const REVIEW_EXPLANATION_PREFIX = "Items marked ";
const REVIEW_EXPLANATION_SUFFIX =
  " are AI-drafted. Accept or edit an item to mark it as reviewed. Importing again only updates AI-drafted items you haven't reviewed. Reviewed and manual items are never overwritten.";

const formatItemCountPhrase = (count: number, verb: string): string =>
  `${count} ${count === 1 ? "item" : "items"} ${count === 1 ? "was" : "were"} ${verb}.`;

const getResultKey = (analysis: ISowAnalysis): string =>
  analysis.runId ?? `${analysis.startedAt ?? ""}:${analysis.completedAt ?? ""}`;

const readDismissedResultKey = (workspaceId: string): string | null => {
  try {
    return window.sessionStorage.getItem(
      `${DISMISSED_RESULT_STORAGE_KEY_PREFIX}:${workspaceId}`,
    );
  } catch {
    // Storage is unavailable (private mode, quota): the dismissal lasts for this view only.
    return null;
  }
};

const writeDismissedResultKey = (
  workspaceId: string,
  resultKey: string,
): void => {
  try {
    window.sessionStorage.setItem(
      `${DISMISSED_RESULT_STORAGE_KEY_PREFIX}:${workspaceId}`,
      resultKey,
    );
  } catch {
    // Storage is unavailable: the dismissal lasts for this view only.
  }
};

interface IImportResultContent {
  tone: "success" | "warning" | "error";
  icon: JSX.Element;
  title: string;
  detail: string;
  showExplanation: boolean;
  failedWarning: string | null;
  showCheckStatus: boolean;
}

export const ImportEngagementItems = ({
  workspaceId,
  analysis,
  isPlanEmpty,
  checkIsPlanEmpty,
  isImportStarting,
  isAnalysisPollTimedOut,
  isStatusUnreachable,
  onImport,
  onCheckStatus,
  onUploadRequested,
}: IImportEngagementItemsProps): JSX.Element => {
  const styles = useImportEngagementItemsStyles();
  const headingId = useId();
  const [isPickerOpen, setIsPickerOpen] = useState<boolean>(false);
  const [selectedFilename, setSelectedFilename] = useState<string | null>(null);
  const [startErrorMessage, setStartErrorMessage] = useState<string>("");
  const [dismissedResultKey, setDismissedResultKey] = useState<string | null>(
    () => readDismissedResultKey(workspaceId),
  );
  const hasOpenedPickerRef = useRef<boolean>(false);
  const focusTargetRef = useRef<HTMLButtonElement>(null);

  const { data: documents, refetch: refetchDocuments } = useQuery<
    IEngagementSourceDocument[]
  >(
    [ENGAGEMENT_SOURCE_DOCUMENTS_QUERY_KEY, workspaceId],
    async () => {
      const libraryDocuments: IEngagementSourceDocument[] =
        await getProjectIqWorkspaceDocuments({ workspaceId });
      return (libraryDocuments ?? []).filter(
        (sourceDocument) => sourceDocument.source !== "kc",
      );
    },
    { enabled: !!workspaceId, refetchOnMount: "always" },
  );

  const { data: tags } = useQuery<IContextFileTag[]>(
    [CONTEXT_FILE_TAGS_QUERY_KEY],
    async () => {
      const contextFileTags = await getProjectIqContextFileTags();
      return Array.isArray(contextFileTags) ? contextFileTags : [];
    },
  );
  const availableTags = useMemo(() => tags ?? [], [tags]);
  const availableDocuments = useMemo(() => documents ?? [], [documents]);

  const selectedDocument = useMemo(
    () =>
      selectedFilename === null
        ? null
        : (availableDocuments.find(
            (sourceDocument) => sourceDocument.filename === selectedFilename,
          ) ?? null),
    [availableDocuments, selectedFilename],
  );

  useEffect(() => {
    if (!documents || selectedFilename === null) return;
    const isStillAvailable = documents.some(
      (sourceDocument) => sourceDocument.filename === selectedFilename,
    );
    if (!isStillAvailable) setSelectedFilename(null);
  }, [documents, selectedFilename]);

  useEffect(() => {
    if (analysis && RESULT_STATUSES.includes(analysis.status)) {
      setStartErrorMessage("");
    }
  }, [analysis?.status]);

  useEffect(() => {
    if (!isPickerOpen && hasOpenedPickerRef.current) {
      focusTargetRef.current?.focus();
    }
  }, [isPickerOpen]);

  const resultKey = analysis ? getResultKey(analysis) : null;
  const isProcessing = analysis?.status === PROCESSING_STATUS;
  const isTimedOutProcessing = isProcessing && isAnalysisPollTimedOut;

  const [hasWatchedImport, setHasWatchedImport] = useState<boolean>(false);
  useEffect(() => {
    if (isProcessing) setHasWatchedImport(true);
  }, [isProcessing]);

  const getResultContent = (): IImportResultContent | null => {
    if (!analysis) return null;
    if (analysis.status === "completed") {
      if (!hasWatchedImport) return null;
      const addedCount = analysis.itemsAdded;
      const updatedCount = analysis.itemsUpdated ?? 0;
      const failedCount = analysis.itemsFailed ?? 0;
      const addedSentence =
        addedCount === undefined
          ? "New items have been added to your plan."
          : formatItemCountPhrase(addedCount, "added");
      const updatedSentence =
        updatedCount > 0
          ? ` ${formatItemCountPhrase(updatedCount, "updated")}`
          : "";
      return {
        tone: "success",
        icon: <CheckmarkCircleFilled />,
        title: "Import complete.",
        detail: `${addedSentence}${updatedSentence}`,
        showExplanation: true,
        failedWarning:
          failedCount > 0
            ? `${failedCount} ${failedCount === 1 ? "item" : "items"} could not be saved.`
            : null,
        showCheckStatus: false,
      };
    }
    if (analysis.status === "failed") {
      return {
        tone: "error",
        icon: <ErrorCircleRegular />,
        title: "Import failed.",
        detail: analysis.errorMessage || IMPORT_FAILED_FALLBACK_MESSAGE,
        showExplanation: false,
        failedWarning: null,
        showCheckStatus: false,
      };
    }
    if (analysis.status === "skipped_no_content") {
      if (!hasWatchedImport) return null;
      return {
        tone: "warning",
        icon: <WarningRegular />,
        title: "Nothing imported.",
        detail: NO_CONTENT_MESSAGE,
        showExplanation: false,
        failedWarning: null,
        showCheckStatus: false,
      };
    }
    if (isTimedOutProcessing) {
      return {
        tone: "warning",
        icon: <WarningRegular />,
        title: "Still importing.",
        detail: IMPORT_TIMED_OUT_MESSAGE,
        showExplanation: false,
        failedWarning: null,
        showCheckStatus: true,
      };
    }
    return null;
  };

  const resultContent =
    analysis && resultKey !== dismissedResultKey ? getResultContent() : null;

  const displayedFinishedResultKeyRef = useRef<string | null>(null);
  displayedFinishedResultKeyRef.current =
    resultContent && analysis && RESULT_STATUSES.includes(analysis.status)
      ? resultKey
      : null;

  useEffect(
    () => () => {
      if (displayedFinishedResultKeyRef.current !== null) {
        writeDismissedResultKey(
          workspaceId,
          displayedFinishedResultKeyRef.current,
        );
      }
    },
    [workspaceId],
  );

  const resultTitleClassName =
    resultContent?.tone === "success"
      ? styles.resultTitleSuccess
      : resultContent?.tone === "error"
        ? styles.resultTitleError
        : styles.resultTitleWarning;
  const resultIconClassName =
    resultContent?.tone === "success"
      ? styles.resultIconSuccess
      : resultContent?.tone === "error"
        ? styles.resultIconError
        : styles.resultIconWarning;

  const isImportDisabled =
    isImportStarting || (isProcessing && !isAnalysisPollTimedOut);

  const runImport = async (
    sourceDocument: IEngagementSourceDocument,
  ): Promise<void> => {
    const errorMessage = await onImport(sourceDocument);
    setStartErrorMessage(errorMessage ?? "");
  };

  const handleSourceDocumentConfirmed = async (
    sourceDocument: IEngagementSourceDocument,
  ): Promise<void> => {
    setSelectedFilename(sourceDocument.filename);
    setStartErrorMessage("");
    setIsPickerOpen(false);
    const shouldAutoStart = checkIsPlanEmpty
      ? await checkIsPlanEmpty()
      : isPlanEmpty;
    if (shouldAutoStart) await runImport(sourceDocument);
  };

  const handleOpenPicker = (): void => {
    hasOpenedPickerRef.current = true;
    setIsPickerOpen(true);
    void refetchDocuments();
  };

  const handleClearSelection = (): void => {
    setSelectedFilename(null);
    setStartErrorMessage("");
  };

  const handleImportClick = (): void => {
    if (!selectedDocument || isImportDisabled) return;
    void runImport(selectedDocument);
  };

  const handleDismissResult = (): void => {
    if (resultKey === null) return;
    writeDismissedResultKey(workspaceId, resultKey);
    setDismissedResultKey(resultKey);
    setStartErrorMessage("");
  };

  const selectedTypeLabel = selectedDocument
    ? getDocumentTypeLabel(selectedDocument.documentType, availableTags)
    : null;

  return (
    <section className={styles.root} aria-labelledby={headingId}>
      <h2 id={headingId} className={styles.heading}>
        Import Engagement Items
      </h2>
      <p className={styles.description}>{DESCRIPTION_TEXT}</p>
      <div className={styles.card}>
        {selectedDocument ? (
          <div className={styles.selectedRow}>
            <div className={styles.selectedBox}>
              <div className={styles.selectedInfo}>
                <div className={styles.selectedNameRow}>
                  <p className={styles.selectedName}>
                    {selectedDocument.originalFilename}
                  </p>
                  {selectedDocument.documentType && selectedTypeLabel && (
                    <SourceDocumentTypeBadge
                      documentType={selectedDocument.documentType}
                      label={selectedTypeLabel}
                    />
                  )}
                </div>
                <p className={styles.selectedFolder}>
                  {getFullFolderPathLabel(selectedDocument.filename)}
                </p>
              </div>
              <Button
                appearance="subtle"
                icon={<DismissRegular />}
                aria-label={CLEAR_SELECTION_LABEL}
                onClick={handleClearSelection}
              />
            </div>
            <Button
              ref={focusTargetRef}
              appearance="primary"
              className={styles.importButton}
              icon={
                isImportStarting ? (
                  <Spinner size="tiny" />
                ) : (
                  <ArrowDownloadRegular />
                )
              }
              disabled={isImportDisabled}
              onClick={handleImportClick}
            >
              {IMPORT_BUTTON_LABEL}
            </Button>
          </div>
        ) : (
          <div>
            <Button
              ref={focusTargetRef}
              appearance="outline"
              icon={<SearchRegular />}
              onClick={handleOpenPicker}
            >
              {BROWSE_BUTTON_LABEL}
            </Button>
          </div>
        )}

        {startErrorMessage && (
          <p role="alert" className={styles.errorText}>
            {startErrorMessage}
          </p>
        )}

        {isStatusUnreachable && (
          <div className={styles.resultRow}>
            <p role="alert" className={styles.errorText}>
              {UNREACHABLE_MESSAGE}
            </p>
            <Button
              appearance="secondary"
              size="small"
              icon={<ArrowClockwiseRegular />}
              onClick={onCheckStatus}
            >
              {CHECK_STATUS_LABEL}
            </Button>
          </div>
        )}

        <div className={styles.resultRegion} role="status" aria-live="polite">
          {resultContent && (
            <div className={styles.resultRow}>
              <span
                className={mergeClasses(styles.resultIcon, resultIconClassName)}
                aria-hidden="true"
              >
                {resultContent.icon}
              </span>
              <div className={styles.resultBody}>
                <span>
                  <span
                    className={mergeClasses(
                      styles.resultTitle,
                      resultTitleClassName,
                    )}
                  >
                    {resultContent.title}
                  </span>{" "}
                  {resultContent.detail}
                  {resultContent.failedWarning && (
                    <span className={styles.resultWarningText}>
                      {" "}
                      {resultContent.failedWarning}
                    </span>
                  )}
                </span>
                {resultContent.showExplanation && (
                  <span className={styles.resultExplanation}>
                    {REVIEW_EXPLANATION_PREFIX}
                    <SparkleRegular
                      className={styles.sparkleIcon}
                      aria-hidden="true"
                    />
                    {REVIEW_EXPLANATION_SUFFIX}
                  </span>
                )}
                {resultContent.showCheckStatus && (
                  <Button
                    appearance="secondary"
                    size="small"
                    icon={<ArrowClockwiseRegular />}
                    onClick={onCheckStatus}
                  >
                    {CHECK_STATUS_LABEL}
                  </Button>
                )}
              </div>
              <Button
                appearance="subtle"
                size="small"
                icon={<DismissRegular />}
                aria-label={DISMISS_RESULT_LABEL}
                onClick={handleDismissResult}
              />
            </div>
          )}
        </div>
      </div>

      {isPickerOpen && (
        <SourceDocumentPickerDialog
          documents={availableDocuments}
          tags={availableTags}
          isLoading={!documents}
          isPlanEmpty={isPlanEmpty}
          initialSelectedFilename={selectedFilename}
          onConfirm={(sourceDocument) => {
            void handleSourceDocumentConfirmed(sourceDocument);
          }}
          onCancel={() => setIsPickerOpen(false)}
          onUploadRequested={() => {
            setIsPickerOpen(false);
            onUploadRequested();
          }}
        />
      )}
    </section>
  );
};
